// Prices the proof options rendered by snippets/cart-proof-options.liquid, with
// the same rules as the live cart page:
// - "Print Proof Photos by Email" keeps one print proof per 5 screens in the cart.
// - "Physical Sample" keeps one sample fee per sample requested.
// The choices are saved as cart attributes so they reach the order.
{
    const selectors = {
        container: "[data-cart-proofs]",
        options: "[data-cart-proof-options]",
        sampleQuantity: "[data-sample-quantity]",
        drawer: '[data-drawer="drawer-cart"]',
    };
    const fields = {
        proof: "attributes[Proof Option]",
        apparel: "attributes[Apparel Proof Option]",
        sampleQuantity: "attributes[Physical Sample Quantity]",
    };
    const PRINT_PROOF = "Print Proof Photos by Email";
    const PHYSICAL_SAMPLE = "Physical Sample";
    const SCREENS_PER_PROOF = 5;

    let queue = Promise.resolve();
    const enqueue = (task) => {
        queue = queue
            .then(task)
            .catch((error) => console.error("Cart proof options:", error));
    };

    const readState = (options) => {
        const field = (name) => options.querySelector(`[name="${name}"]`);
        const checked = (name) =>
            options.querySelector(`[name="${name}"]:checked`)?.value ?? "";
        const hasApparel = Boolean(field(fields.apparel));
        const apparelOption = hasApparel ? checked(fields.apparel) : "";
        const sampleQuantity = hasApparel
            ? (field(fields.sampleQuantity)?.value.trim() ?? "")
            : "";

        return {
            frames: Number(options.dataset.frames) || 0,
            proofVariant: Number(options.dataset.proofVariant) || 0,
            sampleVariant: Number(options.dataset.sampleVariant) || 0,
            proofOption: checked(fields.proof),
            apparelOption,
            sampleQuantity,
            samples:
                apparelOption === PHYSICAL_SAMPLE
                    ? Math.max(parseInt(sampleQuantity, 10) || 0, 0)
                    : 0,
        };
    };

    const reconcile = async (state) => {
        const cart = await fetch(`${window.theme.routes.cart}.js`).then(
            (response) => response.json(),
        );

        const wanted = new Map();
        if (state.proofVariant) {
            wanted.set(
                state.proofVariant,
                state.proofOption === PRINT_PROOF
                    ? Math.ceil(state.frames / SCREENS_PER_PROOF)
                    : 0,
            );
        }
        if (state.sampleVariant) wanted.set(state.sampleVariant, state.samples);

        const updates = {};
        for (const [variantId, quantity] of wanted) {
            const inCart = cart.items
                .filter((item) => item.variant_id === variantId)
                .reduce((total, item) => total + item.quantity, 0);
            if (inCart !== quantity) updates[variantId] = quantity;
        }

        const attributes = {
            "Proof Option": state.proofOption,
            "Apparel Proof Option": state.apparelOption,
            "Physical Sample Quantity": state.sampleQuantity,
        };
        const attributesChanged = Object.entries(attributes).some(
            ([name, value]) => String(cart.attributes[name] ?? "") !== value,
        );
        const itemsChanged = Object.keys(updates).length > 0;

        if (!itemsChanged && !attributesChanged) return;

        const response = await fetch(`${window.theme.routes.cart}/update.js`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            body: JSON.stringify({ updates, attributes }),
        });
        if (!response.ok)
            throw new Error(`cart update failed (${response.status})`);
        const updatedCart = await response.json();

        if (itemsChanged) {
            document.dispatchEvent(
                new CustomEvent("theme:cart:change", {
                    detail: { cart: updatedCart },
                    bubbles: true,
                }),
            );
            document.dispatchEvent(
                new CustomEvent("theme:cart:reload", { bubbles: true }),
            );
        }
    };

    const render = async () => {
        const containers = document.querySelectorAll(selectors.container);
        if (!containers.length) return null;

        const response = await fetch(
            `${window.theme.routes.root_url}?section_id=api-cart-proofs`,
        );
        const html = new DOMParser().parseFromString(
            await response.text(),
            "text/html",
        );
        const fresh = html.querySelector(selectors.options);
        if (!fresh) return null;

        for (const container of containers) {
            container.replaceChildren(fresh.cloneNode(true));
        }
        return containers[0].querySelector(selectors.options);
    };

    const refresh = () =>
        enqueue(async () => {
            const options = await render();
            if (options) await reconcile(readState(options));
        });

    document.addEventListener("theme:cart:change", refresh);
    document
        .querySelector(selectors.drawer)
        ?.addEventListener("theme:drawer:open", refresh);

    document.addEventListener("change", (event) => {
        const options = event.target.closest(selectors.options);
        if (
            !options ||
            !event.target.matches(
                `[name="${fields.proof}"], [name="${fields.apparel}"], [name="${fields.sampleQuantity}"]`,
            )
        )
            return;

        const quantity = options.querySelector(selectors.sampleQuantity);
        if (quantity)
            quantity.hidden =
                readState(options).apparelOption !== PHYSICAL_SAMPLE;

        const state = readState(options);
        enqueue(() => reconcile(state));
    });

    const initial = document.querySelector(
        `${selectors.container} ${selectors.options}`,
    );
    if (initial?.querySelector("fieldset")) {
        const state = readState(initial);
        enqueue(() => reconcile(state));
    }
}
